"use client";

import React, { useRef } from "react";
import { cn } from "utils/utils";
import { useBottomBarInset } from "modules/common/hooks/useBottomBarInset";

interface DockProps {
  children: React.ReactNode;
  size?: "dock-xs" | "dock-sm" | "dock-md" | "dock-lg";
  className?: string;
  /**
   * Rendered as a row directly above the dock items — the anonymous
   * <VisitorCtaBar variant="stacked">. Not a child of `.dock`: daisyUI forces
   * `& > *` to `flex-basis:100%; height:100%` in a row, so a header child
   * would render *beside* the items. It goes in a fixed wrapper instead, and
   * that wrapper is what registers with `useBottomBarInset` — measuring the
   * header and the dock as one bar, since the hook publishes the MAX across
   * bars rather than the sum.
   */
  header?: React.ReactNode;
}

export function Dock({
  children,
  size = "dock-sm",
  className,
  header,
}: DockProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Keeps data-view-panel from ending underneath the dock. The header appears
  // once Clerk resolves, growing this element; the hook's ResizeObserver
  // republishes the taller stack.
  useBottomBarInset(ref);

  if (!header) {
    return (
      <div ref={ref} className={cn("dock z-30", size, className)}>
        {children}
      </div>
    );
  }

  return (
    // `className` carries the breakpoint, so it belongs on the wrapper — the
    // whole stack hides together. `.dock` is position:fixed; inside the
    // wrapper it lays out in flow so the header sits above it.
    <div
      ref={ref}
      className={cn("fixed bottom-0 left-0 right-0 z-30", className)}
    >
      {header}
      <div className={cn("dock [position:static]!", size)}>{children}</div>
    </div>
  );
}
